<script setup>
import GuestLayout from '@/Layouts/GuestLayout.vue';
import InputError from '@/Components/InputError.vue';
import InputLabel from '@/Components/InputLabel.vue';
import PrimaryButton from '@/Components/PrimaryButton.vue';
import TextInput from '@/Components/TextInput.vue';
import GoogleAuthButton from '@/Components/Marketing/GoogleAuthButton.vue';
import {Head, Link, useForm} from '@inertiajs/vue3';

const props = defineProps({
    referral_code: {
        type: String,
        default: ''
    }
});

const form = useForm({
    name: '',
    email: '',
    password: '',
    referral_code: props.referral_code
});

const submit = () => {
    form.post(route('register'), {
        onFinish: () => form.reset('password'),
    });
};
</script>

<template>
    <GuestLayout>
        <Head title="Create your free account"/>

        <div class="text-center">
            <h1 class="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Create your free account</h1>
            <p class="mt-1 text-sm text-gray-600 dark:text-gray-300">
                5 AI speaking sessions with scored feedback. No card needed.
            </p>
        </div>

        <div class="mt-6">
            <GoogleAuthButton location="register_page"/>
        </div>

        <div class="my-6 flex items-center gap-3 text-xs uppercase tracking-wide text-gray-400">
            <span class="h-px flex-1 bg-gray-200 dark:bg-gray-700"></span>
            or sign up with email
            <span class="h-px flex-1 bg-gray-200 dark:bg-gray-700"></span>
        </div>

        <form @submit.prevent="submit">
            <div>
                <InputLabel for="name" value="Name"/>

                <TextInput
                    id="name"
                    v-model="form.name"
                    autocomplete="name"
                    class="mt-1 block w-full"
                    name="name"
                    required
                    type="text"
                />

                <InputError :message="form.errors.name" class="mt-2"/>
            </div>

            <div class="mt-4">
                <InputLabel for="email" value="Email"/>

                <TextInput
                    id="email"
                    v-model="form.email"
                    autocomplete="email"
                    class="mt-1 block w-full"
                    name="email"
                    required
                    type="email"
                />

                <InputError :message="form.errors.email" class="mt-2"/>
            </div>

            <div class="mt-4">
                <InputLabel for="password" value="Password"/>

                <TextInput
                    id="password"
                    v-model="form.password"
                    autocomplete="new-password"
                    class="mt-1 block w-full"
                    name="password"
                    required
                    show-password-toggle
                    type="password"
                />
                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">At least 8 characters.</p>

                <InputError :message="form.errors.password" class="mt-2"/>
            </div>

            <input type="hidden" v-model="form.referral_code">

            <div v-if="form.referral_code" class="mt-4 text-sm text-gray-600 dark:text-gray-300">
                Referred by: {{ form.referral_code }}
            </div>
            <InputError :message="form.errors.referral_code" class="mt-2"/>

            <PrimaryButton :class="{ 'opacity-25': form.processing }" :disabled="form.processing"
                           class="mt-6 w-full justify-center py-3">
                Create free account
            </PrimaryButton>

            <p class="mt-4 text-center text-xs text-gray-500 dark:text-gray-400">
                By signing up you agree to our
                <Link :href="route('page.terms_of_use')" class="underline hover:text-gray-700 dark:hover:text-gray-200">Terms of Use</Link>
                and
                <Link :href="route('page.privacy_policy')" class="underline hover:text-gray-700 dark:hover:text-gray-200">Privacy Policy</Link>.
            </p>
        </form>

        <p class="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?
            <Link :href="route('login')"
                  class="font-medium text-primary-700 underline hover:text-primary-900 dark:text-primary-400">
                Log in
            </Link>
        </p>
    </GuestLayout>
</template>
